import React, { useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import WatermarkOverlay from './WatermarkOverlay';

export default function MediaLightbox({ media, currentIndex, onClose, onNext, onPrev }) {
  const currentItem = media[currentIndex];

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') onClose();
    if (e.key === 'ArrowRight') onNext();
    if (e.key === 'ArrowLeft') onPrev();
  }, [onClose, onNext, onPrev]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [handleKeyDown]);

  const preventSave = (e) => {
    e.preventDefault();
    return false;
  };

  const isYouTube = currentItem?.src && (currentItem.src.includes('youtube') || currentItem.src.includes('youtu.be'));
  const videoSrc = isYouTube ? `${currentItem.src}?autoplay=1&rel=0` : currentItem.src;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] bg-slate-950/95 backdrop-blur-xl flex items-center justify-center p-4 sm:p-12"
        onClick={onClose}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-6 right-6 z-[110] p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
        >
          <SafeIcon icon={FiIcons.FiX} className="w-6 h-6" />
        </button>

        {/* Content Container */}
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="relative max-w-7xl w-full h-full flex items-center justify-center"
          onClick={(e) => e.stopPropagation()}
        >
          {currentItem.type === 'video' ? (
            <div className="w-full aspect-video bg-black rounded-lg overflow-hidden shadow-2xl">
              <iframe
                src={videoSrc}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                allowFullScreen
                title="Video Preview"
              ></iframe>
            </div>
          ) : (
            <div className="relative group flex items-center justify-center h-full w-full">
              <img
                src={currentItem.src}
                alt={currentItem.alt || "Fullscreen Preview"}
                className="max-w-full max-h-full object-contain rounded-lg shadow-2xl select-none"
                onContextMenu={preventSave}
                draggable="false"
              />
              {currentItem.watermark && <WatermarkOverlay />}
            </div>
          )}

          {/* Navigation Controls */}
          {media.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); onPrev(); }}
                className="absolute left-0 top-1/2 -translate-y-1/2 p-4 text-white/50 hover:text-white transition-colors hidden sm:block"
              >
                <SafeIcon icon={FiIcons.FiChevronLeft} className="w-12 h-12" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); onNext(); }}
                className="absolute right-0 top-1/2 -translate-y-1/2 p-4 text-white/50 hover:text-white transition-colors hidden sm:block"
              >
                <SafeIcon icon={FiIcons.FiChevronRight} className="w-12 h-12" />
              </button>
            </>
          )}
        </motion.div>

        {/* Counter */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/60 text-sm font-medium">
          {currentIndex + 1} / {media.length}
        </div>
      </motion.div>
    </AnimatePresence>
  );
}